import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
  type Messaging,
} from 'firebase/messaging';
import { getFirebaseApp, isFirebaseConfigured } from './config';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

async function ensureMessaging(): Promise<Messaging | null> {
  if (!isFirebaseConfigured) return null;
  const app = getFirebaseApp();
  const supported = await isSupported().catch(() => false);
  if (!app || !supported) return null;
  return getMessaging(app);
}

// Requests notification permission (if needed) and returns an FCM registration
// token, or null when unavailable/denied. Register this token with the backend.
export async function requestFcmToken(): Promise<string | null> {
  const messaging = await ensureMessaging();
  if (!messaging || typeof Notification === 'undefined') return null;

  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission().catch(() => 'denied');
  if (permission !== 'granted') return null;

  try {
    return await getToken(messaging, VAPID_KEY ? { vapidKey: VAPID_KEY } : undefined);
  } catch {
    return null;
  }
}

// True only when the browser already granted notification permission, so we can
// silently re-register a token without prompting.
export function hasNotificationPermission(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

export async function onForegroundMessage(
  handler: (payload: MessagePayload) => void,
): Promise<() => void> {
  const messaging = await ensureMessaging();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
